import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button, IconButton, Typography } from "@mhome/ui";
import Snackbar from "@mui/material/Snackbar";
import FixedSelectInput from "../input/fixed-select-input";
import BoolInput from "../input/bool-input";
import TextInput from "../input/text-input";
import NumberInput from "../input/number-input";
import NumberUnitInput from "../input/number-unit-input";
import TimeInput from "../input/time-input";
import DateInput from "../input/date-input";
import TimestampInput from "../input/timestamp-input";
import CloseIcon from "@mui/icons-material/Close";
import { replyDialogUserInput } from "../service/notification-service";
import { useEventEmitter } from "../ws/event-context";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import calendar from "dayjs/plugin/calendar";
import NotificationDialogList from "./notification-dialog-list";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  platformState,
  activeScopeState,
  safeAreaInsetsState,
  dialogsState,
} from "../global-state";
import useApiCall from "../common/api-call";
import { useCustomFormat } from "./after-init";
import { prepareDialog } from "../common/dialog-util";
import { useAppInterface } from "../app/app-interface-provider";

dayjs.extend(relativeTime);
dayjs.extend(calendar);

const NOTIFICATION_SETTINGS_KEY = "notification_settings";

const getNotificationSettings = () => {
  const saved = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);
  return saved
    ? JSON.parse(saved)
    : {
        desktopNotificationEnabled: false,
        notificationPermission: "default",
        systemNotificationsEnabled: false,
      };
};

const getSelector = (dialog) => {
  // Check for options first, regardless of dataType
  if (dialog.options && dialog.options.length > 0) {
    // If repeated is true, use multi-select, otherwise use fixed select
    return dialog.repeated ? "MULTI_SELECT" : "FIXED_SELECT";
  } else if (dialog.select) {
    // Legacy support for old dialog structure
    return "FIXED_SELECT";
  } else if (dialog.dataType === "Bool") {
    return "BOOL_INPUT";
  } else if (dialog.dataType === "String") {
    return "TEXT_INPUT";
  } else if (dialog.dataType === "Number") {
    return "NUMBER_INPUT";
  } else if (
    dialog.dataType === "Weight" ||
    dialog.dataType === "Length" ||
    dialog.dataType === "Temperature" ||
    dialog.dataType === "Duration" ||
    dialog.dataType === "TemperatureDiff"
  ) {
    return "NUMBER_UNIT_INPUT";
  } else if (dialog.dataType === "Time") {
    return "TIME_INPUT";
  } else if (dialog.dataType === "Date") {
    return "DATE_INPUT";
  } else if (dialog.dataType === "Timestamp") {
    return "TIMESTAMP_INPUT";
  } else {
    throw new Error("unsupported");
  }
};

const getNeedConfirm = (dialog) => {
  return true;
};

const updateDialogValue = (dialog, value) => {
  if (dialog.dataType === "Bool") {
    dialog.value = value;
  } else if (dialog.dataType === "String") {
    dialog.value = value;
  } else if (dialog.dataType === "Number") {
    dialog.value = value;
  } else if (dialog.dataType === "Temperature") {
    dialog.value = value;
  } else if (dialog.dataType === "Duration") {
    dialog.value = value;
  } else if (dialog.dataType === "TemperatureDiff") {
    dialog.value = value;
  } else if (dialog.dataType === "Time") {
    dialog.value = value;
  } else if (dialog.dataType === "Date") {
    dialog.value = value;
  } else if (dialog.dataType === "Timestamp") {
    dialog.value = value;
  } else if (dialog.options !== undefined) {
    dialog.value = value;
  }
};

const notificationPredicate = (a, b) => a && b && a.id === b.id;

const dialogPredicate = (a, b) => a && b && a.id === b.id;

const GroupedDataSelector = ({ dialogs, onConfirm, onIgnore }) => {
  const [preparedDialogs, setPreparedDialogs] = useState([]);

  const selectMap = {
    FIXED_SELECT: (dialog) => (
      <FixedSelectInput
        valueSpec={dialog}
        onChange={(value) => handleValueChange(dialog, value)}
        onError={() => handleErrorChange(dialog)}
        zIndex="var(--z-index-snackbar-input)"
      />
    ),
    MULTI_SELECT: (dialog) => (
      <FixedSelectInput
        valueSpec={dialog}
        onChange={(value) => handleValueChange(dialog, value)}
        onError={() => handleErrorChange(dialog)}
        multiSelect={true}
        zIndex="var(--z-index-snackbar-input)"
      />
    ),
    BOOL_INPUT: (dialog) => (
      <BoolInput
        valueSpec={dialog}
        onChange={(value) => handleValueChange(dialog, value)}
      />
    ),
    TEXT_INPUT: (dialog) => (
      <TextInput
        valueSpec={dialog}
        onChange={(value) => handleValueChange(dialog, value)}
        style="standard"
      />
    ),
    NUMBER_INPUT: (dialog) => (
      <NumberInput
        valueSpec={dialog}
        onChange={(value) => handleValueChange(dialog, value)}
        onError={() => {}}
      />
    ),
    NUMBER_UNIT_INPUT: (dialog) => (
      <NumberUnitInput
        valueSpec={dialog}
        onChange={(value, unit, max, min) =>
          handleValueChange(dialog, value, unit, max, min)
        }
        onError={() => {}}
        zIndex="var(--z-index-snackbar-input)"
      />
    ),
    TIME_INPUT: (dialog) => (
      <TimeInput
        valueSpec={dialog}
        onChange={(value) => handleValueChange(dialog, value)}
        embedded={false}
        zIndex="var(--z-index-snackbar-input)"
      />
    ),
    DATE_INPUT: (dialog) => (
      <DateInput
        valueSpec={dialog}
        onChange={(value) => handleValueChange(dialog, value)}
        zIndex="var(--z-index-snackbar-input)"
      />
    ),
    TIMESTAMP_INPUT: (dialog) => (
      <TimestampInput
        valueSpec={dialog}
        onChange={(value) => handleValueChange(dialog, value)}
        embedded={false}
        zIndex="var(--z-index-snackbar-input)"
      />
    ),
  };

  const [dialogStates, setDialogStates] = useState({});

  useEffect(() => {
    // Initialize dialog states for each dialog
    const pd = dialogs.map((d) => prepareDialog(d));
    setPreparedDialogs(pd);
    const initialStates = {};
    pd.forEach((dialog) => {
      initialStates[dialog.id] = {
        selector: getSelector(dialog),
        needConfirm: getNeedConfirm(dialog),
        value: dialog.value,
        error: null,
      };
    });
    setDialogStates(initialStates);
  }, [dialogs]);

  const handleValueChange = async (dialog, newValue, unit, max, min) => {
    // For NumberUnitInput components, combine value and unit
    const finalValue = unit ? `${newValue}${unit}` : newValue;

    const newDialogState = {
      ...dialogStates[dialog.id],
      value: finalValue,
      error: false,
    };

    console.debug("handleValueChange", newDialogState);

    setDialogStates((prev) => ({
      ...prev,
      [dialog.id]: newDialogState,
    }));

    if (newDialogState && !newDialogState.needConfirm) {
      await onConfirm(dialog, newDialogState.value);
    }
  };

  const handleErrorChange = (dialog) => {
    setDialogStates((prev) => ({
      ...prev,
      [dialog.id]: {
        ...prev[dialog.id],
        error: true,
      },
    }));
  };

  const handleConfirm = async (dialog) => {
    const dialogState = dialogStates[dialog.id];
    if (dialogState && dialogState.error) {
      return;
    }
    await onConfirm(dialog, dialogState?.value);
  };

  const handleIgnore = (dialog) => {
    onIgnore(dialog);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        minWidth: "300px",
      }}
    >
      {preparedDialogs.map((dialog, index) => {
        const dialogState = dialogStates[dialog.id];
        if (!dialogState) return null;

        return (
          <div
            key={dialog.id}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <Typography
              variant="body2"
              style={{
                fontWeight: 500,
                color: "hsl(var(--foreground))",
                whiteSpace: "pre-wrap",
              }}
            >
              {dialog.message}
            </Typography>

            {selectMap[dialogState.selector] && (
              <div
                style={{
                  minHeight: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "16px",
                }}
              >
                {selectMap[dialogState.selector](dialog)}
              </div>
            )}

            {dialogState.needConfirm && (
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  justifyContent: "space-between",
                }}
              >
                <Button
                  size="medium"
                  variant="outlined"
                  fullWidth
                  onClick={() => handleIgnore(dialog)}
                >
                  Cancel
                </Button>
                <Button
                  size="medium"
                  variant="contained"
                  fullWidth
                  disabled={
                    !dialogState.value ||
                    dialogState.value === "" ||
                    dialogState.error
                  }
                  style={{
                    boxShadow: "none",
                  }}
                  onClick={() => handleConfirm(dialog)}
                >
                  Confirm
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MAX_NOTIFICATIONS = 100;

const handleStorageNotifications = (notifications) => {
  const sortedNotifications = notifications.sort((a, b) => {
    return new Date(b.timestamp) - new Date(a.timestamp);
  });

  const limitedNotifications = sortedNotifications.slice(0, MAX_NOTIFICATIONS);

  localStorage.setItem("notifications", JSON.stringify(limitedNotifications));

  return limitedNotifications;
};

const NotificationDialogSystem = ({
  listOpen,
  setListOpen,
  setNotificationStatus,
  setNotificationCount,
}) => {
  const [dialogs, setDialogs] = useRecoilState(dialogsState);
  const [dialog, setDialog] = useState(null);
  const [dialogGroup, setDialogGroup] = useState([]);
  const [notification, setNotification] = useState(null);
  const [notifications, setNotifications] = useState(() => {
    const savedNotifications = localStorage.getItem("notifications");
    const parsedNotifications = savedNotifications
      ? JSON.parse(savedNotifications)
      : [];
    return handleStorageNotifications(parsedNotifications);
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeScope] = useRecoilState(activeScopeState);
  const [platform] = useRecoilState(platformState);
  const { apiCall } = useApiCall();
  const { subscribe } = useEventEmitter();
  const [currentTime, setCurrentTime] = useState(dayjs());
  const customFormat = useCustomFormat();
  const appInterface = useAppInterface();
  const dialogTimerRef = useRef(null);
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);

  const [notificationSettings, setNotificationSettings] = useState(
    getNotificationSettings
  );

  const formatCountdown = (remainingSeconds) => {
    if (remainingSeconds <= 0) return "Expired";

    const days = Math.floor(remainingSeconds / (24 * 60 * 60));
    const hours = Math.floor((remainingSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((remainingSeconds % (60 * 60)) / 60);
    const seconds = remainingSeconds % 60;

    if (days > 0) {
      return `${days} day${days > 1 ? "s" : ""}`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""}`;
    } else if (minutes > 0) {
      return `${minutes} min`;
    } else {
      return `${seconds}s`;
    }
  };

  const getDialogRemainingTime = (dialog) => {
    if (!dialog || !dialog.createdAt || !dialog.timeout) return 0;
    const now = dayjs();
    const expiryTime = dayjs(dialog.createdAt * 1000).add(
      dialog.timeout,
      "second"
    );
    return Math.max(0, expiryTime.diff(now, "second"));
  };

  const sendDesktopNotification = (notification, onClick = null) => {
    if (!notificationSettings.desktopNotificationEnabled) return;
    if (!notification) return;

    try {
      // Pass the entire notification object, same as what internal popup uses
      appInterface.sendNotification(notification);

      // Set up one-time click listener for this notification
      if (onClick) {
        const clickHandler = () => {
          console.debug("clickHandler");
          onClick();
          appInterface.removeEventListener(
            "notification-clicked",
            clickHandler
          );
        };
        appInterface.addEventListener("notification-clicked", clickHandler);
      }
    } catch (error) {
      console.error("Error creating notification:", error);
    }
  };

  const handleSubscription = (target, data) => {
    if (target === "/dialog") {
      const payload = JSON.parse(data.payload);
      receiveDialog(payload);
    } else if (target === "/notification") {
      const payload = JSON.parse(data.payload);
      payload.fromEvent = true;
      if (payload.realtime || data.realtime) {
        // Show snackbar but don't add to list - chat-view will handle regular notifications
        setNotification(payload);
        sendDesktopNotification(payload, () =>
          handleNotificationClick(payload)
        );
      }
    }
  };

  const handleEventEmission = (eventType, data) => {
    if (eventType === "dialog-click") {
      // Handle dialog click events from chat-block.js
      const clickedDialog = data.dialog;
      if (clickedDialog) {
        handleDialogClick(clickedDialog);
      }
    }
  };

  const dialogsRef = useRef(dialogs);

  // Keep the ref synchronized with dialogs state
  useEffect(() => {
    dialogsRef.current = dialogs;
  }, [dialogs]);

  const showNextDialog = useCallback(() => {
    // Use the ref to get the current dialogs state
    const currentDialogs = dialogsRef.current;

    // Filter out dismissed dialogs for auto-showing
    const undismissedDialogs = currentDialogs.filter((d) => !d.dismissed);

    // Find the next dialog to show from the undismissed dialogs
    // Sort dialogs by creation time (oldest first) to maintain order
    const sortedDialogs = [...undismissedDialogs].sort(
      (a, b) => a.createdAt - b.createdAt
    );

    if (sortedDialogs.length > 0) {
      const nextDialog = sortedDialogs[0];
      // Find all undismissed dialogs with the same groupId
      const groupedDialogs = undismissedDialogs.filter(
        (d) => d.groupId === nextDialog.groupId
      );
      // Sort by id to ensure consistent ordering
      groupedDialogs.sort((a, b) => a.id.localeCompare(b.id));

      setDialog(nextDialog);
      setDialogGroup(groupedDialogs);
      setDrawerOpen(true);
    } else {
      setDrawerOpen(false);
      setDialog(null);
      setDialogGroup([]);
    }
  }, []);

  useEffect(() => {
    const { unsubscribe } = subscribe(handleSubscription);
    const { unsubscribe: unsubscribeEvents } = subscribe(handleEventEmission);

    return () => {
      if (dialogTimerRef.current) {
        clearInterval(dialogTimerRef.current);
      }
      if (unsubscribe) {
        unsubscribe();
      }
      if (unsubscribeEvents) {
        unsubscribeEvents();
      }
    };
  }, []);

  useEffect(() => {
    let status = 0;
    if (dialogs.length > 0) {
      status = status | 2;
    }
    if (notifications.length > 0) {
      if (notifications.some((n) => n.isSystem)) {
        status = status | 4;
      }
      if (notifications.some((n) => !n.isSystem)) {
        status = status | 1;
      }
    }
    setNotificationStatus(status);

    if (!notifications.some((n) => notificationPredicate(n, notification))) {
      setNotification(null);
    }

    if (dialog && !dialogs.some((n) => dialogPredicate(n, dialog))) {
      hideDialog();
    }

    // If there are dialogs but no dialog is currently shown, show the first one
    if (dialogs.length > 0 && !dialog && !drawerOpen) {
      const timeoutId = setTimeout(() => {
        showNextDialog();
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return () => {};
  }, [dialogs, notifications, dialog, drawerOpen, showNextDialog]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Timer for dialog countdown - updates every second when dialog is active
  useEffect(() => {
    if (dialog) {
      const timer = setInterval(() => {
        setCurrentTime(dayjs());

        // Check if the current dialog has expired
        if (dialog && getDialogRemainingTime(dialog) <= 0) {
          console.debug("Dialog expired, hiding");
          setDrawerOpen(false);
          setDialog(null);
          setDialogGroup([]);
          // Handle timeout for the expired dialog
          handleDialogTimeout(dialog);
        }
      }, 1000);

      dialogTimerRef.current = timer;

      return () => {
        if (dialogTimerRef.current) {
          clearInterval(dialogTimerRef.current);
          dialogTimerRef.current = null;
        }
      };
    } else {
      if (dialogTimerRef.current) {
        clearInterval(dialogTimerRef.current);
        dialogTimerRef.current = null;
      }
    }
  }, [dialog]);

  useEffect(() => {
    if (platform.source === "desktop") {
      const handleUpdateAvailable = (info) => {
        console.debug("update:available info:", info);
        if (info || info.version) {
          const updateNotification = {
            id: "software-update-available",
            title: "Software Update Available",
            message: `New version ${info.version} is available. Please update.`,
            timestamp: new Date().toISOString(),
            isSystem: true,
            systemLevel: 2,
            isPinned: true,
            persistent: true,
            version: info.version,
            updateInfo: info,
          };

          setNotifications((prevNotifications) => {
            const existingUpdateNotifications = prevNotifications.filter(
              (n) =>
                n.id === "software-update-available" ||
                n.id === "software-update-ready"
            );

            const isDuplicateNotification = existingUpdateNotifications.some(
              (n) =>
                n.version === info.version &&
                n.id === "software-update-available"
            );

            if (!isDuplicateNotification) {
              sendDesktopNotification(updateNotification, () => {
                history.push("/setting#software-update");
              });

              localStorage.setItem(
                "mandatoryUpdateInfo",
                JSON.stringify({
                  version: info.version,
                  timestamp: Date.now(),
                  available: true,
                  ...info,
                })
              );

              const filteredNotifications = prevNotifications.filter(
                (n) =>
                  n.id !== "software-update-available" &&
                  n.id !== "software-update-ready"
              );

              const newNotifications = [
                updateNotification,
                ...filteredNotifications,
              ];

              return handleStorageNotifications(newNotifications);
            }

            return prevNotifications;
          });
        }
      };

      const handleUpdateReady = (info) => {
        console.debug("update:ready info:", info);
        if (info || info.version) {
          const updateReadyNotification = {
            id: "software-update-ready",
            title: "Software Update Ready",
            message: `Version ${info.version} is downloaded and ready to install.`,
            timestamp: new Date().toISOString(),
            isSystem: true,
            systemLevel: 2,
            isPinned: true,
            persistent: true,
            version: info.version,
            updateInfo: info,
          };

          setNotifications((prevNotifications) => {
            const existingUpdateNotifications = prevNotifications.filter(
              (n) =>
                n.id === "software-update-available" ||
                n.id === "software-update-ready"
            );

            const isDuplicateNotification = existingUpdateNotifications.some(
              (n) =>
                n.version === info.version && n.id === "software-update-ready"
            );

            if (!isDuplicateNotification) {
              localStorage.setItem(
                "mandatoryUpdateInfo",
                JSON.stringify({
                  version: info.version,
                  timestamp: Date.now(),
                  ready: true,
                  ...info,
                })
              );

              sendDesktopNotification(updateReadyNotification, () => {
                history.push("/setting#software-update");
              });

              const filteredNotifications = prevNotifications.filter(
                (n) =>
                  n.id !== "software-update-available" &&
                  n.id !== "software-update-ready"
              );

              const newNotifications = [
                updateReadyNotification,
                ...filteredNotifications,
              ];

              return handleStorageNotifications(newNotifications);
            }

            return prevNotifications;
          });
        }
      };

      const handleUpdateNotAvailable = () => {
        // Remove update notifications when we're already up to date
        setNotifications((prevNotifications) => {
          const filteredNotifications = prevNotifications.filter(
            (n) =>
              n.id !== "software-update-available" &&
              n.id !== "software-update-ready"
          );
          return handleStorageNotifications(filteredNotifications);
        });

        // Clear the current notification if it's an update notification
        setNotification((currentNotification) => {
          if (
            currentNotification &&
            (currentNotification.id === "software-update-available" ||
              currentNotification.id === "software-update-ready")
          ) {
            return null;
          }
          return currentNotification;
        });
      };

      appInterface.addEventListener("update:available", handleUpdateAvailable);
      appInterface.addEventListener("update:ready", handleUpdateReady);
      appInterface.addEventListener("update:latest", handleUpdateNotAvailable);

      return () => {
        appInterface.removeEventListener(
          "update:available",
          handleUpdateAvailable
        );
        appInterface.removeEventListener("update:ready", handleUpdateReady);
        appInterface.removeEventListener(
          "update:latest",
          handleUpdateNotAvailable
        );
      };
    }
  }, [activeScope, history]);

  const receiveDialog = (dialogChanges) => {
    console.debug("receiveDialog:", dialogChanges);

    // Check for new dialogs before state update
    const currentDialogs = dialogsRef.current;
    let hasNewDialog = false;

    for (const d of dialogChanges.changes) {
      if (d.type === "UPSERT") {
        d.dialog.title = "Action Required";
        d.dialog.scopeId = dialogChanges.scopeId;
        d.dialog.isDialog = true;

        // Check if this is a new dialog
        const existingDialog = currentDialogs.find(
          (dd) => dd.scopeId === dialogChanges.scopeId && dd.id === d.dialog.id
        );

        if (!existingDialog) {
          hasNewDialog = true;
          sendDesktopNotification(d.dialog, () => showDialog(d.dialog));
          setNotification(null);
        }
      }
    }

    setDialogs((prevDialogs) => {
      let newd = [...prevDialogs];

      for (const d of dialogChanges.changes) {
        if (d.type === "DELETE_DIALOG") {
          console.debug("Deleting dialog:", d.deleteDialogId);
          newd = newd.filter(
            (dialog) =>
              dialog.scopeId !== dialogChanges.scopeId ||
              dialog.id !== d.deleteDialogId
          );
        } else if (d.type === "UPSERT") {
          console.debug("Upserting dialog:", d.dialog);
          const idx = newd.findIndex(
            (dd) =>
              dd.scopeId === dialogChanges.scopeId && dd.id === d.dialog.id
          );
          if (idx !== -1) {
            console.debug("Updating existing dialog at index:", idx);
            newd[idx] = d.dialog;
          } else {
            console.debug("Adding new dialog");
            newd.push(d.dialog);
          }
        }
      }

      console.debug("Final dialogs array:", newd);
      return newd;
    });

    // If we have a new dialog and no dialog is currently showing, show the first one
    if (hasNewDialog && !drawerOpen && dialogChanges.realtime) {
      const timeoutId = setTimeout(() => {
        showNextDialog();
      }, 100);
    }
  };

  const clearAllNotifications = () => {
    setNotifications((prevNotifications) => {
      // Keep system notifications when clearing all
      const systemAndUpdateNotifications = prevNotifications.filter(
        (n) =>
          n.isSystem ||
          n.id === "software-update-available" ||
          n.id === "software-update-ready"
      );
      localStorage.setItem(
        "notifications",
        JSON.stringify(systemAndUpdateNotifications)
      );
      return systemAndUpdateNotifications;
    });
    setNotification(null);
  };

  const handleNotificationClick = useCallback(
    (notification) => {
      console.debug("handleNotificationClick", notification);
      const notificationToShow = { ...notification, fromEvent: false };
      setNotification(notificationToShow);
    },
    [platform]
  );

  const handleDialogTimeout = (dialog) => {
    setDialogs((prev) => {
      const predicate = (item) => item.id === dialog.id;
      const newd = prev.filter((item) => !predicate(item));
      return newd;
    });

    // Show next dialog in queue after a short delay
    setTimeout(() => {
      showNextDialog();
    }, 100);
  };

  const handleDialogClick = useCallback((clickedDialog) => {
    // Find the current dialog in the dialogs state (in case the clicked one is stale)
    const currentDialog = dialogsRef.current.find(
      (d) => d.id === clickedDialog.id
    );
    if (currentDialog) {
      showDialog(currentDialog);
    }
  }, []);

  const showDialog = (dialog) => {
    // Only show if no dialog is currently displayed, otherwise it will be queued
    if (!dialog || drawerOpen) {
      return;
    }

    // Clear dismissed flag for manually opened dialogs
    if (dialog.dismissed) {
      setDialogs((prevDialogs) => {
        const newDialogs = prevDialogs.map((item) => {
          if (item.id === dialog.id) {
            return { ...item, dismissed: false };
          }
          return item;
        });
        return newDialogs;
      });
    }

    // Find all dialogs with the same groupId from current dialogs state
    const currentDialogs = dialogsRef.current;
    const groupedDialogs = currentDialogs.filter(
      (d) => d.groupId === dialog.groupId
    );
    // Sort by id to ensure consistent ordering
    groupedDialogs.sort((a, b) => a.id.localeCompare(b.id));

    setDialog(dialog);
    setDialogGroup(groupedDialogs);
    setDrawerOpen(true);
  };

  const handleDialogIgnore = (dialog) => {
    // Mark the dialog as dismissed in storage
    setDialogs((prevDialogs) => {
      const newDialogs = prevDialogs.map((item) => {
        if (item.id === dialog.id) {
          return { ...item, dismissed: true };
        }
        return item;
      });
      return newDialogs;
    });

    // Remove from current group display
    setDialogGroup((prevGroup) => {
      const newGroup = prevGroup.filter((item) => item.id !== dialog.id);

      // If no more dialogs in the group, close the drawer and show next
      if (newGroup.length === 0) {
        setDrawerOpen(false);
        setDialog(null);
        setDialogGroup([]);

        // Show next dialog in queue after a short delay
        setTimeout(() => {
          showNextDialog();
        }, 100);
      }

      return newGroup;
    });
  };

  const hideDialog = () => {
    setDrawerOpen(false);
    setDialog(null);
    setDialogGroup([]);

    // Show next dialog in queue after a short delay
    const timeoutId = setTimeout(() => {
      showNextDialog();
    }, 100);
  };

  const handleDialogConfirm = async (dialog, value) => {
    // Update the dialog value and submit
    updateDialogValue(dialog, value);
    await replyDialogUserInput(apiCall, dialog.scopeId, dialog);

    // Remove the confirmed dialog from the state
    setDialogs((prevDialogs) => {
      const newDialogs = prevDialogs.filter((item) => item.id !== dialog.id);
      return newDialogs;
    });

    // Update the current dialog group by removing the confirmed dialog
    setDialogGroup((prevGroup) => {
      const newGroup = prevGroup.filter((item) => item.id !== dialog.id);

      // If no more dialogs in the group, close the drawer and show next
      if (newGroup.length === 0) {
        setDrawerOpen(false);
        setDialog(null);
        setDialogGroup([]);

        // Show next dialog in queue after a short delay
        setTimeout(() => {
          showNextDialog();
        }, 100);
      }

      return newGroup;
    });
  };

  const handleCloseNotification = (item) => {
    if (item.isPinned) {
      setNotification(null);
      return;
    }

    if (item.isAnnounce) {
      handleNotificationAck(item);
      return;
    }

    if (item.isDialog) {
      // Handle dialog deletion
      const wasCurrentDialog = dialog && dialog.id === item.id;

      setDialogs((prevDialogs) => {
        const predicate = (d) => d.id === item.id;
        const newDialogs = prevDialogs.filter((d) => !predicate(d));
        return newDialogs;
      });

      // If this was the current dialog, hide it and show next
      if (wasCurrentDialog) {
        setDrawerOpen(false);
        setDialog(null);
        setDialogGroup([]);
        setTimeout(() => {
          showNextDialog();
        }, 100);
      }
    } else {
      // Handle regular notification deletion
      setNotifications((prevNotifications) => {
        const predicate = (n) => notificationPredicate(n, item);
        const newNotifications = prevNotifications.filter((n) => !predicate(n));
        localStorage.setItem("notifications", JSON.stringify(newNotifications));
        return newNotifications;
      });

      // If the deleted notification is currently being displayed, clear it
      if (notification && notificationPredicate(notification, item)) {
        setNotification(null);
      }
    }
  };

  const handleNotificationAck = async (notification) => {
    if (notification.isSystem) return;

    const ackBody = {
      scopeId: activeScope,
      announceId: notification.announceId || notification.id,
    };
    console.debug("ack body:", ackBody, "notification:", notification);

    try {
      await apiCall("/announce/ack", "POST", ackBody, "user");

      setNotifications((prevNotifications) =>
        prevNotifications.filter((n) => n.id !== notification.id)
      );

      if (notificationPredicate(notification, notification)) {
        setNotification(null);
      }
    } catch (error) {
      console.error("Error acknowledging notification:", error);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await apiCall("/announce/get", "GET", null, "user");

      if (response) {
        const announcements = response.map((announcement) => ({
          id: announcement.id,
          announceId: announcement.id,
          title: announcement.title,
          message: announcement.content,
          timestamp: announcement.createdAt,
          isAnnounce: true,
          fromEvent: false,
        }));

        setNotifications((prevNotifications) => {
          const nonAnnouncementNotifications = prevNotifications.filter(
            (n) => !n.isAnnounce
          );
          return handleStorageNotifications([
            ...announcements,
            ...nonAnnouncementNotifications,
          ]);
        });
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  };

  // useEffect(() => {
  //   if (activeScope) {
  //     fetchAnnouncements();
  //   }
  // }, []);

  return (
    <>
      <div>
        {dialog && (
          <Snackbar
            open={drawerOpen}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            autoHideDuration={null}
            style={{
              width: "auto",
            }}
            ContentProps={{
              style: {
                marginTop: `${safeAreaInsets.top}px`,
                borderRadius: "16px",
                width: "auto",
                minWidth: "80vw",
                backgroundColor: "hsl(var(--card))",
                color: "hsl(var(--foreground))",
                padding: "16px",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
              },
            }}
            messageProps={{
              style: {
                width: "100%",
                padding: 0,
              },
            }}
            ClickAwayListenerProps={{
              disabled: true,
            }}
            onClose={(event, reason) => {
              console.debug("onClose called with reason:", reason);
              // Only close on escape key since clickaway is disabled
              if (reason === "escapeKeyDown") {
                hideDialog();
              }
            }}
            message={
              <div style={{ flexGrow: 1, width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{
                      color: "hsl(var(--muted-foreground))",
                    }}
                  >
                    {customFormat(
                      dayjs((dialog?.createdAt || 0) * 1000),
                      currentTime
                    )}
                  </Typography>
                  <div
                    style={{
                      paddingLeft: "12px",
                      paddingRight: "12px",
                      paddingTop: "4px",
                      paddingBottom: "4px",
                      borderRadius: "16px",
                      backgroundColor: (() => {
                        // Use the dialog's remaining time
                        const remaining = dialog
                          ? getDialogRemainingTime(dialog)
                          : 0;
                        if (remaining <= 0) return "hsl(var(--destructive))";
                        if (remaining <= 60) return "hsl(var(--destructive))";
                        if (remaining <= 600) return "hsl(var(--warning))";
                        return "hsl(var(--primary))";
                      })(),
                      color: (() => {
                        const remaining = dialog
                          ? getDialogRemainingTime(dialog)
                          : 0;
                        if (remaining <= 0)
                          return "hsl(var(--destructive-foreground))";
                        if (remaining <= 60)
                          return "hsl(var(--destructive-foreground))";
                        if (remaining <= 600)
                          return "hsl(var(--warning-foreground))";
                        return "hsl(var(--primary-foreground))";
                      })(),
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      textAlign: "center",
                      minWidth: "60px",
                      animation:
                        dialog && getDialogRemainingTime(dialog) <= 60
                          ? "pulse 1s infinite"
                          : "none",
                    }}
                  >
                    <style>
                      {`
                        @keyframes pulse {
                          0% { opacity: 1; }
                          50% { opacity: 0.7; }
                          100% { opacity: 1; }
                        }
                      `}
                    </style>
                    {formatCountdown(
                      dialog ? getDialogRemainingTime(dialog) : 0
                    )}
                  </div>
                </div>
                <div
                  role="presentation"
                  style={{ paddingLeft: "16px", paddingRight: "16px" }}
                >
                  <GroupedDataSelector
                    dialogs={dialogGroup}
                    onConfirm={handleDialogConfirm}
                    onIgnore={handleDialogIgnore}
                  />
                </div>
              </div>
            }
          />
        )}
      </div>

      {notification && (
        <Snackbar
          open={true}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={notification.fromEvent ? 10000 : null}
          style={{
            width: "auto",
          }}
          ContentProps={{
            style: {
              marginTop: `${safeAreaInsets.top}px`,
              borderRadius: "16px",
              width: "auto",
              minWidth: "80vw",
              backgroundColor: "hsl(var(--card))",
              color: "hsl(var(--foreground))",
              padding: "16px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
            },
          }}
          messageProps={{
            style: {
              width: "100%",
              padding: 0,
            },
          }}
          onClose={() => {
            setNotification(null);
          }}
          message={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderRadius: "16px",
              }}
            >
              <div>
                <Typography
                  variant="caption"
                  style={{
                    marginBottom: "16px",
                    display: "block",
                  }}
                >
                  {customFormat(notification.timestamp, currentTime)}
                </Typography>
                <Typography variant="h6">{notification.title}</Typography>
                <Typography style={{ whiteSpace: "pre-wrap" }}>
                  {notification.message}
                </Typography>
              </div>
              <div>
                <div>
                  <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={() => handleCloseNotification(notification)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            </div>
          }
        />
      )}
      <NotificationDialogList
        notifications={notifications}
        setNotification={setNotification}
        dialogs={dialogs}
        onClose={() => setListOpen(false)}
        onNotificationClick={handleNotificationClick}
        onDialogTimeout={handleDialogTimeout}
        onDialogClick={handleDialogClick}
        onClearNotifications={clearAllNotifications}
        open={listOpen}
        onDelete={handleCloseNotification}
        setNotificationCount={setNotificationCount}
      />
    </>
  );
};

export default NotificationDialogSystem;
